/**
 * @fileoverview Utilidades para creación y manejo de errores de geocodificación
 * @module types/errors/errorHandlers
 * @version 1.0.0
 */

import type {
  GeocodingError,
  GeocodingSource,
  GeocodingLevel,
  ErrorContext,
  ErrorSeverity,
  CrossValidationError,
  NetworkError,
  ValidationError,
  SourceError,
  SystemError,
  GeocodingResult,
  ResultMetadata,
  InfrastructureTypology,
} from './GeocodingErrors';

// =====================================================
// FACTORY: CREAR CONTEXTO
// =====================================================

/**
 * Crea un contexto de error con valores por defecto
 */
export function createErrorContext(
  source: GeocodingSource,
  level: GeocodingLevel,
  partial?: Partial<ErrorContext>
): ErrorContext {
  return {
    timestamp: new Date(),
    source,
    level,
    attemptNumber: partial?.attemptNumber ?? 1,
    correlationId: partial?.correlationId ?? generateCorrelationId(),
    ...partial,
  };
}

/**
 * Genera ID único para correlación de trazas
 */
function generateCorrelationId(): string {
  return `geo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// =====================================================
// FACTORY: CREAR ERRORES
// =====================================================

/**
 * Factory functions para crear errores tipados
 * Garantizan que todos los campos requeridos estén presentes
 */
export const createError = {
  // --- Errores de Red ---
  
  timeout: (
    context: ErrorContext,
    endpoint: string,
    timeoutMs: number
  ) => ({
    kind: 'timeout' as const,
    severity: 'recoverable' as const,
    context,
    endpoint,
    timeoutMs,
    message: `Timeout después de ${timeoutMs}ms en ${endpoint}`,
  }),

  serviceUnavailable: (
    context: ErrorContext,
    endpoint: string,
    httpStatus?: number,
    retryAfter?: number
  ) => ({
    kind: 'service_unavailable' as const,
    severity: 'recoverable' as const,
    context,
    endpoint,
    httpStatus,
    retryAfter,
    message: `Servicio no disponible: ${endpoint}${httpStatus ? ` (HTTP ${httpStatus})` : ''}`,
  }),

  rateLimit: (
    context: ErrorContext,
    endpoint: string,
    retryAfter: number,
    dailyLimitRemaining?: number
  ) => ({
    kind: 'rate_limit' as const,
    severity: 'recoverable' as const,
    context,
    endpoint,
    retryAfter,
    dailyLimitRemaining,
    message: `Límite de peticiones excedido. Reintentar en ${retryAfter}s`,
  }),

  cors: (context: ErrorContext, endpoint: string) => ({
    kind: 'cors' as const,
    severity: 'fatal' as const,
    context,
    endpoint,
    message: `Error CORS bloqueó la petición a ${endpoint}`,
  }),

  connection: (
    context: ErrorContext,
    endpoint: string,
    connectionType: 'dns' | 'refused' | 'reset' | 'unknown'
  ) => ({
    kind: 'connection' as const,
    severity: 'recoverable' as const,
    context,
    endpoint,
    connectionType,
    message: `Error de conexión (${connectionType}) a ${endpoint}`,
  }),

  // --- Errores de Validación ---

  invalidCoordinate: (
    context: ErrorContext,
    coordinate: { x?: unknown; y?: unknown },
    reason: 'nan' | 'negative_utm' | 'swapped' | 'truncated' | 'zero' | 'malformed'
  ) => ({
    kind: 'invalid_coordinate' as const,
    severity: 'degraded' as const,
    context,
    coordinate,
    reason,
    message: `Coordenada inválida: ${reason}`,
  }),

  outOfBounds: (
    context: ErrorContext,
    coordinate: { x: number; y: number },
    distanceOutside: number,
    possibleWrongZone = false
  ) => ({
    kind: 'out_of_bounds' as const,
    severity: 'degraded' as const,
    context,
    coordinate,
    expectedBounds: {
      minX: 100000,
      maxX: 620000,
      minY: 3980000,
      maxY: 4290000,
    },
    distanceOutside,
    possibleWrongZone,
    message: `Coordenadas fuera de Andalucía (${distanceOutside.toFixed(0)}m fuera de límites)`,
  }),

  ineMismatch: (
    context: ErrorContext,
    expectedINE: string,
    returnedINE: string,
    expectedMunicipality: string,
    returnedMunicipality: string,
    discrepancyKm: number
  ) => ({
    kind: 'ine_mismatch' as const,
    severity: 'degraded' as const,
    context,
    expectedINE,
    returnedINE,
    expectedMunicipality,
    returnedMunicipality,
    discrepancyKm,
    message: `INE incorrecto: esperado ${expectedMunicipality} (${expectedINE}), recibido ${returnedMunicipality} (${returnedINE}) - ${discrepancyKm.toFixed(0)}km de discrepancia`,
  }),

  formatParse: (
    context: ErrorContext,
    rawValue: string,
    expectedFormat: 'utm' | 'wgs84' | 'dms' | 'ed50' | 'mgrs',
    parseAttempts: string[]
  ) => ({
    kind: 'format_parse' as const,
    severity: 'recoverable' as const,
    context,
    rawValue,
    expectedFormat,
    parseAttempts,
    message: `No se pudo parsear "${rawValue}" como ${expectedFormat}`,
  }),

  utf8Corruption: (
    context: ErrorContext,
    originalText: string,
    correctedText?: string,
    patternsApplied: string[] = [],
    patternTier?: 'hot' | 'warm' | 'cold'
  ) => ({
    kind: 'utf8_corruption' as const,
    severity: 'degraded' as const,
    context,
    originalText,
    correctedText,
    patternsApplied,
    patternTier,
    message: correctedText
      ? `Texto corregido: "${originalText}" → "${correctedText}"`
      : `Corrupción UTF-8 no recuperable: "${originalText}"`,
  }),

  // --- Errores de Fuente ---

  noResults: (
    context: ErrorContext,
    searchQuery: string,
    filtersApplied?: Record<string, string>,
    sourceResponded = true
  ) => ({
    kind: 'no_results' as const,
    severity: 'recoverable' as const,
    context,
    searchQuery,
    filtersApplied,
    sourceResponded,
    message: `Sin resultados para "${searchQuery}" en ${context.source}`,
  }),

  ambiguousMatch: (
    context: ErrorContext,
    candidates: Array<{ name: string; score: number; coordinates: { x: number; y: number } }>,
    selectedIndex?: number
  ) => ({
    kind: 'ambiguous_match' as const,
    severity: 'degraded' as const,
    context,
    candidates,
    selectedIndex,
    scoreDifference: candidates.length >= 2
      ? candidates[0].score - candidates[1].score
      : undefined,
    message: `${candidates.length} candidatos ambiguos encontrados`,
  }),

  lowConfidence: (
    context: ErrorContext,
    score: number,
    threshold: number,
    factors: Array<{ factor: string; contribution: number; detail?: string }>
  ) => ({
    kind: 'low_confidence' as const,
    severity: 'degraded' as const,
    context,
    score,
    threshold,
    factors,
    message: `Score ${score} por debajo del umbral ${threshold}`,
  }),

  // --- Errores de Validación Cruzada ---

  sourceDiscrepancy: (
    context: ErrorContext,
    sources: Array<{
      source: GeocodingSource;
      coordinates: { x: number; y: number };
      confidence: number;
    }>,
    maxDiscrepancy: number,
    meanDiscrepancy: number,
    huberCentroid?: { x: number; y: number },
    resolution: 'accepted_huber' | 'accepted_highest' | 'accepted_official' | 'manual_required' = 'manual_required'
  ) => ({
    kind: 'source_discrepancy' as const,
    severity: 'degraded' as const,
    context,
    sources,
    maxDiscrepancy,
    meanDiscrepancy,
    huberCentroid,
    simpleCentroid: calculateSimpleCentroid(sources.map(s => s.coordinates)),
    resolution,
    message: `Discrepancia de ${maxDiscrepancy.toFixed(0)}m entre ${sources.length} fuentes`,
  }),

  municipalityMismatch: (
    context: ErrorContext,
    expectedMunicipio: string,
    expectedINE: string,
    coordinates: { x: number; y: number },
    distanceToBoundary: number,
    actualMunicipio?: string,
    actualINE?: string,
    isAdjacentMunicipality = false
  ) => ({
    kind: 'municipality_mismatch' as const,
    severity: 'degraded' as const,
    context,
    expectedMunicipio,
    expectedINE,
    actualMunicipio,
    actualINE,
    coordinates,
    distanceToBoundary,
    possibleBoundaryCase: distanceToBoundary < 500,
    isAdjacentMunicipality,
    message: `Coordenadas en ${actualMunicipio ?? 'municipio desconocido'}, esperado ${expectedMunicipio}`,
  }),

  distanceThreshold: (
    context: ErrorContext,
    tipologia: InfrastructureTypology,
    coordinates: { x: number; y: number },
    nearestNeighborDistance: number,
    expectedRange: { min: number; max: number },
    isStatisticalOutlier: boolean,
    lofScore?: number,
    neighborsAnalyzed = 5
  ) => ({
    kind: 'distance_threshold' as const,
    severity: 'degraded' as const,
    context,
    tipologia,
    coordinates,
    nearestNeighborDistance,
    expectedRange,
    isStatisticalOutlier,
    lofScore,
    neighborsAnalyzed,
    message: `Distancia anómala: ${nearestNeighborDistance.toFixed(0)}m (esperado ${expectedRange.min}-${expectedRange.max}m)`,
  }),

  inconsistentAddress: (
    context: ErrorContext,
    originalAddress: string,
    reverseAddress: string,
    similarity: number,
    differences: Array<{
      component: 'street' | 'number' | 'municipality' | 'postal_code' | 'province';
      original: string;
      reverse: string;
    }>
  ) => ({
    kind: 'inconsistent_address' as const,
    severity: 'degraded' as const,
    context,
    originalAddress,
    reverseAddress,
    similarity,
    differences,
    message: `Dirección inconsistente (similitud ${(similarity * 100).toFixed(0)}%)`,
  }),

  outlierDetected: (
    context: ErrorContext,
    coordinates: { x: number; y: number },
    detectionMethod: 'lof' | 'zscore' | 'iqr' | 'huber',
    anomalyScore: number,
    threshold: number,
    expectedCluster?: { centroid: { x: number; y: number }; radius: number }
  ) => ({
    kind: 'outlier_detected' as const,
    severity: 'degraded' as const,
    context,
    coordinates,
    detectionMethod,
    anomalyScore,
    threshold,
    expectedCluster,
    message: `Outlier detectado (${detectionMethod}): score ${anomalyScore.toFixed(2)} > umbral ${threshold}`,
  }),

  // --- Errores de Sistema ---

  cache: (
    context: ErrorContext,
    operation: 'read' | 'write' | 'invalidate' | 'clear',
    cacheKey: string,
    cacheType: 'memory' | 'indexeddb' | 'localstorage' = 'indexeddb'
  ) => ({
    kind: 'cache' as const,
    severity: 'recoverable' as const,
    context,
    operation,
    cacheKey,
    cacheType,
    message: `Error de caché (${operation}) para clave ${cacheKey}`,
  }),

  transformation: (
    context: ErrorContext,
    fromEPSG: string,
    toEPSG: string,
    coordinate: { x: number; y: number },
    proj4Error?: string
  ) => ({
    kind: 'transformation' as const,
    severity: 'fatal' as const,
    context,
    fromEPSG,
    toEPSG,
    coordinate,
    proj4Error,
    message: `Error transformando de ${fromEPSG} a ${toEPSG}`,
  }),

  configuration: (
    context: ErrorContext,
    missingConfig: string,
    expectedType: string,
    actualValue?: unknown
  ) => ({
    kind: 'configuration' as const,
    severity: 'fatal' as const,
    context,
    missingConfig,
    expectedType,
    actualValue,
    message: `Configuración inválida: ${missingConfig} (esperado ${expectedType})`,
  }),

  internal: (
    context: ErrorContext,
    originalError?: Error
  ) => ({
    kind: 'internal' as const,
    severity: 'fatal' as const,
    context,
    originalError,
    stack: originalError?.stack,
    message: originalError?.message ?? 'Error interno desconocido',
  }),
};

// =====================================================
// TYPE GUARDS
// =====================================================

/**
 * Verifica si el error es recuperable (puede intentar siguiente fuente)
 */
export function isRecoverable(error: GeocodingError): boolean {
  return error.severity === 'recoverable';
}

/**
 * Verifica si el error es degradado (funciona pero con menor calidad)
 */
export function isDegraded(error: GeocodingError): boolean {
  return error.severity === 'degraded';
}

/**
 * Verifica si el error es fatal (no se puede continuar)
 */
export function isFatal(error: GeocodingError): boolean {
  return error.severity === 'fatal';
}

/**
 * Type guard para errores de red
 */
export function isNetworkError(error: GeocodingError): error is NetworkError {
  return ['timeout', 'service_unavailable', 'rate_limit', 'cors', 'connection'].includes(error.kind);
}

/**
 * Type guard para errores de validación
 */
export function isValidationError(error: GeocodingError): error is ValidationError {
  return ['invalid_coordinate', 'out_of_bounds', 'format_parse', 'utf8_corruption', 'invalid_input'].includes(error.kind);
}

/**
 * Type guard para errores de fuente
 */
export function isSourceError(error: GeocodingError): error is SourceError {
  return ['no_results', 'ambiguous_match', 'low_confidence', 'invalid_response'].includes(error.kind);
}

/**
 * Type guard para errores de validación cruzada
 */
export function isCrossValidationError(error: GeocodingError): error is CrossValidationError {
  return [
    'source_discrepancy',
    'municipality_mismatch',
    'distance_threshold',
    'inconsistent_address',
    'outlier_detected',
  ].includes(error.kind);
}

/**
 * Type guard para errores de sistema
 */
export function isSystemError(error: GeocodingError): error is SystemError {
  return ['cache', 'transformation', 'configuration', 'internal'].includes(error.kind);
}

// =====================================================
// ACCIONES RECOMENDADAS
// =====================================================

export type RecommendedAction =
  | { action: 'retry'; delayMs: number; maxRetries: number }
  | { action: 'try_next_source' }
  | { action: 'accept_with_warning'; confidence: 'low' | 'medium' }
  | { action: 'use_huber_centroid'; centroid: { x: number; y: number } }
  | { action: 'manual_review'; priority: 'low' | 'medium' | 'high' | 'critical' }
  | { action: 'abort'; logLevel: 'warn' | 'error' };

/**
 * Obtiene la acción recomendada según el tipo de error
 */
export function getRecommendedAction(error: GeocodingError): RecommendedAction {
  switch (error.kind) {
    // Errores de red - reintentar con backoff
    case 'timeout':
      return { action: 'retry', delayMs: 2000, maxRetries: 3 };

    case 'service_unavailable':
      return {
        action: 'retry',
        delayMs: error.retryAfter ? error.retryAfter * 1000 : 5000,
        maxRetries: 2,
      };

    case 'rate_limit':
      return { action: 'retry', delayMs: error.retryAfter * 1000, maxRetries: 1 };

    case 'connection':
      return { action: 'retry', delayMs: 3000, maxRetries: 2 };

    case 'cors':
      return { action: 'abort', logLevel: 'error' };

    // Errores de validación
    case 'invalid_coordinate':
      return { action: 'try_next_source' };

    case 'out_of_bounds':
      return error.possibleWrongZone
        ? { action: 'manual_review', priority: 'medium' }
        : { action: 'abort', logLevel: 'warn' };

    case 'format_parse':
      return { action: 'try_next_source' };

    case 'utf8_corruption':
      return error.correctedText
        ? { action: 'accept_with_warning', confidence: 'medium' }
        : { action: 'try_next_source' };

    // Errores de fuente
    case 'no_results':
      return { action: 'try_next_source' };

    case 'ambiguous_match':
      return error.scoreDifference && error.scoreDifference > 0.2
        ? { action: 'accept_with_warning', confidence: 'medium' }
        : { action: 'manual_review', priority: 'medium' };

    case 'low_confidence':
      return error.score >= 40
        ? { action: 'accept_with_warning', confidence: 'low' }
        : { action: 'try_next_source' };

    case 'invalid_response':
      return { action: 'try_next_source' };

    // Errores de validación cruzada
    case 'source_discrepancy':
      if (error.huberCentroid && error.maxDiscrepancy < 150) {
        return { action: 'use_huber_centroid', centroid: error.huberCentroid };
      }
      return error.maxDiscrepancy < 100
        ? { action: 'accept_with_warning', confidence: 'medium' }
        : { action: 'manual_review', priority: 'high' };

    case 'municipality_mismatch':
      if (error.possibleBoundaryCase && error.isAdjacentMunicipality) {
        return { action: 'accept_with_warning', confidence: 'low' };
      }
      return { action: 'manual_review', priority: 'critical' };

    case 'distance_threshold':
      return error.isStatisticalOutlier
        ? { action: 'manual_review', priority: 'high' }
        : { action: 'accept_with_warning', confidence: 'medium' };

    case 'inconsistent_address':
      return error.similarity > 0.6
        ? { action: 'accept_with_warning', confidence: 'medium' }
        : { action: 'manual_review', priority: 'medium' };

    case 'outlier_detected':
      return { action: 'manual_review', priority: 'high' };

    // Errores de sistema
    case 'cache':
      return { action: 'try_next_source' };

    case 'transformation':
      return { action: 'abort', logLevel: 'error' };

    case 'configuration':
      return { action: 'abort', logLevel: 'error' };

    case 'internal':
      return { action: 'abort', logLevel: 'error' };

    default:
      return { action: 'try_next_source' };
  }
}

// =====================================================
// PENALIZACIONES DE SCORE
// =====================================================

/**
 * Penalizaciones de score según tipo de error
 * Valores negativos reducen el score
 */
export const SCORE_PENALTIES: Record<GeocodingError['kind'], number> = {
  // Errores de red (no penalizan, se reintenta)
  timeout: 0,
  service_unavailable: 0,
  rate_limit: 0,
  cors: 0,
  connection: 0,

  // Errores de validación
  invalid_coordinate: -15,
  out_of_bounds: -20,
  ine_mismatch: -25,  // Penalización alta: municipio incorrecto
  format_parse: -5,
  utf8_corruption: -2,
  invalid_input: -5,

  // Errores de fuente
  no_results: 0,
  ambiguous_match: -8,
  low_confidence: -10,
  invalid_response: 0,

  // Errores de validación cruzada
  source_discrepancy: -12,
  municipality_mismatch: -15,
  distance_threshold: -10,
  inconsistent_address: -8,
  outlier_detected: -15,

  // Errores de sistema
  cache: 0,
  transformation: -25,
  configuration: 0,
  internal: 0,
};

/**
 * Calcula penalización proporcional para errores de discrepancia
 * A mayor discrepancia, mayor penalización
 */
export function calculateDiscrepancyPenalty(maxDiscrepancy: number): number {
  if (maxDiscrepancy < 25) return 0;      // <25m: sin penalización
  if (maxDiscrepancy < 50) return -3;     // 25-50m: leve
  if (maxDiscrepancy < 100) return -8;    // 50-100m: moderada
  if (maxDiscrepancy < 200) return -12;   // 100-200m: significativa
  return -18;                              // >200m: severa
}

/**
 * Aplica penalizaciones al score base según errores encontrados
 */
export function applyErrorPenalties(
  baseScore: number,
  errors: GeocodingError[]
): number {
  let totalPenalty = 0;

  for (const error of errors) {
    // Penalización base por tipo
    let penalty = SCORE_PENALTIES[error.kind];

    // Penalización proporcional para discrepancias
    if (error.kind === 'source_discrepancy') {
      penalty = calculateDiscrepancyPenalty(error.maxDiscrepancy);
    }

    // Penalización proporcional para municipio incorrecto
    if (error.kind === 'municipality_mismatch') {
      penalty = error.possibleBoundaryCase ? -5 : -15;
    }

    totalPenalty += penalty;
  }

  // Asegurar que el score final esté entre 0 y 100
  return Math.max(0, Math.min(100, baseScore + totalPenalty));
}

// =====================================================
// HELPERS PARA RESULT
// =====================================================

/**
 * Crea un resultado exitoso
 */
export function success<T>(
  data: T,
  metadata?: ResultMetadata,
  warnings?: GeocodingError[]
): GeocodingResult<T> {
  return { success: true, data, metadata, warnings };
}

/**
 * Crea un resultado fallido
 */
export function failure<T>(
  error: GeocodingError,
  partialData?: Partial<T>,
  additionalErrors?: GeocodingError[]
): GeocodingResult<T> {
  return { success: false, error, partialData, additionalErrors };
}

/**
 * Combina múltiples resultados, fallando si alguno falla
 */
export function combineResults<T>(
  results: GeocodingResult<T>[]
): GeocodingResult<T[]> {
  const failures = results.filter((r): r is GeocodingResult<T> & { success: false } => !r.success);

  if (failures.length > 0) {
    return failure(
      failures[0].error,
      undefined,
      failures.slice(1).map(f => f.error)
    );
  }

  const successes = results as Array<GeocodingResult<T> & { success: true }>;
  const allWarnings = successes.flatMap(s => s.warnings ?? []);

  return success(
    successes.map(s => s.data),
    undefined,
    allWarnings.length > 0 ? allWarnings : undefined
  );
}

// =====================================================
// UTILIDADES AUXILIARES
// =====================================================

/**
 * Calcula centroide simple (media aritmética)
 */
function calculateSimpleCentroid(
  points: Array<{ x: number; y: number }>
): { x: number; y: number } | undefined {
  if (points.length === 0) return undefined;

  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);

  return {
    x: sumX / points.length,
    y: sumY / points.length,
  };
}

/**
 * Formatea error para logging
 */
export function formatErrorForLog(error: GeocodingError): string {
  const ctx = error.context;
  const timestamp = ctx.timestamp.toISOString();
  const element = ctx.elementName ?? ctx.elementId ?? 'unknown';

  return `[${timestamp}] ${error.kind.toUpperCase()} (${error.severity}) - ${element} @ ${ctx.source}/${ctx.level}: ${error.message}`;
}

/**
 * Agrupa errores por severidad
 */
export function groupErrorsBySeverity(
  errors: GeocodingError[]
): Record<ErrorSeverity, GeocodingError[]> {
  return {
    recoverable: errors.filter(e => e.severity === 'recoverable'),
    degraded: errors.filter(e => e.severity === 'degraded'),
    fatal: errors.filter(e => e.severity === 'fatal'),
  };
}

/**
 * Cuenta errores por tipo
 */
export function countErrorsByKind(
  errors: GeocodingError[]
): Record<string, number> {
  return errors.reduce((counts, error) => {
    counts[error.kind] = (counts[error.kind] ?? 0) + 1;
    return counts;
  }, {} as Record<string, number>);
}

// =====================================================
// WRAPPER DE ERRORES (Legacy compatibility)
// =====================================================

/**
 * Contexto simplificado para wrapError (compatible con legacy)
 */
export interface WrapErrorContext {
  source?: string;
  query?: string;
  municipio?: string;
  tipologia?: string;
}

/**
 * Convierte un error genérico de JavaScript en un GeocodingError tipado.
 * Útil para wrappear errores de fetch, timeouts, y otras APIs nativas.
 * 
 * @param error - Error original (puede ser Error, DOMException, o cualquier valor)
 * @param context - Contexto opcional para enriquecer el error
 * @returns GeocodingError tipado con discriminated union
 * 
 * @example
 * ```typescript
 * try {
 *   await fetch(url);
 * } catch (error) {
 *   const typedError = wrapError(error, { source: 'CartoCiudad', query: 'Colomera' });
 *   // typedError.kind será 'timeout', 'connection', o 'internal'
 * }
 * ```
 */
export function wrapError(
  error: unknown,
  context: WrapErrorContext = {}
): GeocodingError {
  // Crear contexto completo
  const errorContext = createErrorContext(
    (context.source as GeocodingSource) ?? 'manual',
    'L5', // Nivel por defecto para errores envueltos
    {
      elementName: context.query,
      municipio: context.municipio,
    }
  );

  // Si ya es un GeocodingError, devolverlo tal cual
  if (isGeocodingErrorByKind(error)) {
    return error;
  }

  // Detectar AbortError (timeout de fetch)
  if (error instanceof DOMException && error.name === 'AbortError') {
    return createError.timeout(errorContext, context.source ?? 'unknown', 5000);
  }

  // Detectar errores de red/fetch
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return createError.connection(errorContext, context.source ?? 'unknown', 'unknown');
  }

  // Detectar errores de CORS
  if (
    error instanceof TypeError &&
    (error.message.includes('CORS') || error.message.includes('cross-origin'))
  ) {
    return createError.cors(errorContext, context.source ?? 'unknown');
  }

  // Error genérico de JavaScript
  if (error instanceof Error) {
    return createError.internal(errorContext, error);
  }

  // Fallback para cualquier otro valor
  return {
    kind: 'internal' as const,
    severity: 'fatal' as const,
    context: errorContext,
    message: String(error),
  };
}

/**
 * Type guard para verificar si un valor es un GeocodingError
 * basándose en la propiedad 'kind' (discriminated union)
 */
function isGeocodingErrorByKind(value: unknown): value is GeocodingError {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.kind === 'string' &&
    typeof obj.severity === 'string' &&
    typeof obj.message === 'string' &&
    typeof obj.context === 'object'
  );
}
